import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListSubheader,
  Popover,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import {
  Bell,
  Building2,
  Check,
  CheckCheck,
  ChevronRight,
  ListTodo,
  type LucideIcon,
  MapPin,
  MessageCircle,
  Receipt,
  Settings,
  Share2,
  Star,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatStrictRelativeTime } from '@modules/_shared/utils/formatting';
import type { LocalizedRelease } from '@modules/product-updates/releases/releaseEntries';
import { useReleaseFeed } from '@modules/product-updates/releases/useReleaseFeed';
import {
  WHATS_NEW_BELL_ITEMS,
  WhatsNewList,
} from '@modules/product-updates/releases/WhatsNewList';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { useLocale } from '@shared/hooks/locale';

import {
  type NotificationInboxGroupKey,
  type NotificationInboxIcon,
  type NotificationInboxItem,
  groupNotificationInboxItems,
} from './notificationInboxMessages';
import { useNotificationInbox } from './useNotificationInbox';
import { WHATS_NEW_BELL_ANCHOR } from './whatsNewBellTour';

type Inbox = ReturnType<typeof useNotificationInbox>;
type Section = 'notifications' | 'whats-new';

const ICONS: Record<NotificationInboxIcon, LucideIcon> = {
  lead: Target,
  enquiry: MessageCircle,
  review: Star,
  tasks: ListTodo,
  transaction: Receipt,
  listing: Building2,
  share: Share2,
  valuation: TrendingUp,
  map: MapPin,
};

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

type Filter = 'all' | 'unread';

const ErrorDetail = ({ text }: { text: string }) => (
  <Typography
    variant="caption"
    component="pre"
    sx={{
      m: 0,
      mt: 0.5,
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
    }}
  >
    {text}
  </Typography>
);

const InboxEmptyState = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <Stack
    direction="row"
    sx={{ alignItems: 'center', gap: 1.5, px: 2, py: 2.5 }}
  >
    <Box
      aria-hidden
      sx={theme => ({
        width: 36,
        height: 36,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        bgcolor: alpha(theme.palette.success.main, 0.12),
        color: 'success.main',
      })}
    >
      <Check size={20} strokeWidth={2} />
    </Box>
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {subtitle}
      </Typography>
    </Box>
  </Stack>
);

// The inbox part of the bell: operational notifications read from the
// shared Pro App inbox.
const InboxPanel = ({
  inbox,
  onNavigate,
}: {
  inbox: Inbox;
  onNavigate: (path: string) => void;
}) => {
  const { t, dateLocale } = useLocale();
  const { me } = useAppData();
  const {
    status,
    items,
    unreadCount,
    markOneOpened,
    trackClick,
    markAllOpened,
    reload,
    error,
    actionError,
    dismissActionError,
  } = inbox;
  const [filter, setFilter] = useState<Filter>('all');

  const groupLabels: Record<NotificationInboxGroupKey, string> = {
    today: t('Today'),
    yesterday: t('Yesterday'),
    earlier: t('Earlier'),
  };

  const visibleItems =
    filter === 'unread' ? items.filter(item => !item.opened) : items;
  const groups = groupNotificationInboxItems(visibleItems, new Date());

  const handleOpen = (item: NotificationInboxItem) => {
    trackClick(item.id);
    markOneOpened(item.id);
    if (item.path != null) {
      onNavigate(item.path);
    }
  };

  const settingsPath =
    me != null && (me.is_broker === true || me.is_admin === true)
      ? `/users/${me.id}/overview?notifications=all`
      : null;

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, next: Filter | null) => {
            if (next != null) {
              setFilter(next);
            }
          }}
          aria-label={t('Notifications')}
        >
          <ToggleButton value="all" sx={{ px: 1.5, py: 0.25 }}>
            {t('All')}
          </ToggleButton>
          <ToggleButton value="unread" sx={{ px: 1.5, py: 0.25 }}>
            {unreadCount > 0 ? `${t('Unread')} (${unreadCount})` : t('Unread')}
          </ToggleButton>
        </ToggleButtonGroup>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<CheckCheck size={16} />}
            onClick={() => markAllOpened()}
          >
            {t('Mark all as read')}
          </Button>
        )}
      </Stack>
      {actionError != null && (
        <Alert
          severity="error"
          onClose={dismissActionError}
          sx={{ mx: 2, mb: 1 }}
        >
          {t('Could not update your notifications. Please try again.')}
          <ErrorDetail text={actionError} />
        </Alert>
      )}
      <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
        {status === 'loading' && (
          <Stack spacing={1} sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
          </Stack>
        )}
        {status === 'error' && (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <Button color="inherit" size="small" onClick={reload}>
                {t('Retry')}
              </Button>
            }
          >
            {t('Could not load your notifications.')}
            {error != null && <ErrorDetail text={error} />}
          </Alert>
        )}
        {status === 'ready' &&
          groups.length === 0 &&
          (filter === 'unread' ? (
            <InboxEmptyState
              title={t('No unread notifications')}
              subtitle={t('You read everything. Nice work.')}
            />
          ) : (
            <InboxEmptyState
              title={t("You're all caught up")}
              subtitle={t("We'll let you know when something needs you.")}
            />
          ))}
        {status === 'ready' &&
          groups.map((group, groupIndex) => (
            <List
              key={group.key}
              dense
              disablePadding
              subheader={
                <ListSubheader
                  sx={{ lineHeight: '32px', fontSize: 12, fontWeight: 500 }}
                >
                  {groupLabels[group.key]}
                </ListSubheader>
              }
            >
              {group.items.map((item, itemIndex) => {
                const ItemIcon = ICONS[item.icon];
                const isLastItem =
                  groupIndex === groups.length - 1 &&
                  itemIndex === group.items.length - 1;
                return (
                  <ListItemButton
                    key={item.id}
                    divider={!isLastItem}
                    onClick={() => handleOpen(item)}
                    sx={{
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 1.25,
                      bgcolor: item.opened ? undefined : 'primary.lighter',
                    }}
                  >
                    <Box
                      sx={{
                        pt: '2px',
                        color: item.opened ? 'text.secondary' : 'primary.main',
                      }}
                    >
                      <ItemIcon size={18} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: item.opened ? 400 : 600 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}
                        >
                          {formatStrictRelativeTime(item.sentAt, dateLocale)}
                        </Typography>
                      </Stack>
                      {item.description !== '' && (
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                    {!item.opened && (
                      <Box component="span" sx={visuallyHidden}>
                        {t('Unread')}
                      </Box>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          ))}
      </Box>
      {settingsPath != null && (
        <ListItemButton
          onClick={() => onNavigate(settingsPath)}
          sx={{
            gap: 1,
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        >
          <Settings size={16} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {t('Notification settings')}
          </Typography>
          <ChevronRight size={16} />
        </ListItemButton>
      )}
    </>
  );
};

const countLabel = (label: string, count: number) =>
  count > 0 ? `${label} (${count})` : label;

export const NotificationBell = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const inbox = useNotificationInbox();
  const feed = useReleaseFeed();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [section, setSection] = useState<Section>('notifications');
  // Release ids that were unread when the popover opened (see WhatsNewList).
  const [newReleaseIds, setNewReleaseIds] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const hasInbox = inbox.status !== 'unavailable';
  const hasReleases = feed.releases.length > 0;
  // Plays once when the What's new launch entry goes live.
  useAutoTour('whats-new-bell', hasInbox || hasReleases);
  const activeSection: Section = hasInbox ? section : 'whats-new';
  const isOpen = anchorEl != null;
  const { markSeen, unreadIds, releases } = feed;

  // Looking at the What's new list is what marks its entries as seen.
  useEffect(() => {
    if (!isOpen || activeSection !== 'whats-new') {
      return;
    }
    const shownUnread = releases
      .slice(0, WHATS_NEW_BELL_ITEMS)
      .map(release => release.id)
      .filter(id => unreadIds.has(id));
    if (shownUnread.length > 0) {
      markSeen(shownUnread, 'bell');
    }
  }, [isOpen, activeSection, releases, unreadIds, markSeen]);

  if (!hasInbox && !hasReleases) {
    return null;
  }

  const releaseUnread = feed.unreadIds.size;
  const unreadCount = inbox.unreadCount + releaseUnread;

  const handleOpenPopover = (target: HTMLElement) => {
    setNewReleaseIds(new Set(feed.unreadIds));
    // Open on the section that has something new, notifications first.
    setSection(
      hasInbox && (inbox.unreadCount > 0 || releaseUnread === 0)
        ? 'notifications'
        : 'whats-new',
    );
    setAnchorEl(target);
  };

  const goTo = (path: string) => {
    setAnchorEl(null);
    navigate(path);
  };

  const handleOpenRelease = (release: LocalizedRelease) =>
    goTo(`/whats-new/${release.id}`);

  const bellLabel =
    unreadCount > 0
      ? t('Notifications, {{count}} unread', { count: unreadCount })
      : t('Notifications');

  return (
    <>
      <IconButton
        {...tourAnchor(WHATS_NEW_BELL_ANCHOR)}
        color="inherit"
        aria-label={bellLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title={t('Notifications')}
        onClick={event => handleOpenPopover(event.currentTarget)}
        sx={{ flexShrink: 0, margin: '4px' }}
      >
        <Badge
          color="error"
          badgeContent={unreadCount}
          max={99}
          invisible={unreadCount === 0}
        >
          <Bell size={22} strokeWidth={1.8} />
        </Badge>
      </IconButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': hasInbox ? t('Notifications') : t("What's new"),
            sx: { width: 400, maxWidth: 'calc(100vw - 16px)' },
          },
        }}
      >
        {hasInbox ? (
          <Tabs
            value={activeSection}
            onChange={(_, next: Section) => setSection(next)}
            variant="fullWidth"
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Tab
              value="notifications"
              label={countLabel(t('Notifications'), inbox.unreadCount)}
            />
            <Tab
              value="whats-new"
              label={countLabel(t("What's new"), releaseUnread)}
            />
          </Tabs>
        ) : (
          <Typography
            variant="h6"
            component="h2"
            sx={{ px: 2, pt: 1.5, pb: 1 }}
          >
            {t("What's new")}
          </Typography>
        )}
        {activeSection === 'notifications' ? (
          <InboxPanel inbox={inbox} onNavigate={goTo} />
        ) : (
          <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
            <WhatsNewList
              releases={feed.releases}
              newIds={newReleaseIds}
              onOpen={handleOpenRelease}
              onSeeAll={() => goTo('/whats-new')}
            />
          </Box>
        )}
      </Popover>
    </>
  );
};
